import { useState, useEffect, useRef } from 'react';
import { Plus, Trash, ShieldStar, Users as UsersIcon, X, Image as ImageIcon, Pencil, Link as LinkIcon, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import GraphSetupWizard from '../components/GraphSetupWizard';
import { useProfile } from '../context/ProfileContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProfilesPage() {
  const { refetch: refetchProfileContext } = useProfile();
  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ slug: '', name: '', color: '#0F172A' });
  const [error, setError] = useState('');
  const [editingProfile, setEditingProfile] = useState(null);  // {slug, name, color}
  const fileInputs = useRef({});

  const uploadLogo = async (slug, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`${API}/api/profiles/${slug}/logo`, {
      method: 'POST', credentials: 'include', body: fd,
    });
    if (r.ok) fetchAll();
    else {
      const d = await r.json().catch(() => ({}));
      window.alert(d.detail || 'Upload mislukt');
    }
  };

  const saveEdit = async () => {
    if (!editingProfile) return;
    await fetch(`${API}/api/profiles/${editingProfile.slug}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: editingProfile.name,
        color: editingProfile.color,
        tagline: editingProfile.tagline || '',
        market_description: editingProfile.market_description || '',
        product_context: editingProfile.product_context || '',
        ai_instructions: editingProfile.ai_instructions || '',
        sector_label: editingProfile.sector_label || '',
      }),
    });
    setEditingProfile(null);
    fetchAll();
  };

  // ─── AI Kennis & Context test-preview ──────────────────────────────────
  const [aiTesting, setAiTesting] = useState(false);
  const [aiTestResult, setAiTestResult] = useState(null);
  const [aiSample, setAiSample] = useState({
    sample_subject: 'Vraag over jullie aanbod',
    sample_body: 'Hallo,\n\nIk kwam jullie tegen en heb een vraag over wat jullie aanbieden. Kunnen jullie kort uitleggen wat jullie product/dienst inhoudt en of dit voor ons relevant kan zijn?\n\nMet vriendelijke groet,\nJanneke',
    sample_from: 'janneke@voorbeeld.nl',
  });

  const runAiTest = async () => {
    if (!editingProfile) return;
    setAiTesting(true);
    setAiTestResult(null);
    try {
      const r = await fetch(`${API}/api/profiles/${editingProfile.slug}/ai-context/test`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          market_description: editingProfile.market_description || '',
          product_context: editingProfile.product_context || '',
          ai_instructions: editingProfile.ai_instructions || '',
          ...aiSample,
        }),
      });
      const d = await r.json();
      if (!r.ok) {
        setAiTestResult({ ok: false, error: d.detail || 'Mislukt' });
      } else {
        setAiTestResult(d);
      }
    } catch (e) {
      setAiTestResult({ ok: false, error: 'Verbindingsfout' });
    }
    setAiTesting(false);
  };

  // ─── MS GRAPH credentials per profile ────────────────────────────────
  const [graphProfile, setGraphProfile] = useState(null);  // slug being managed
  const [graphConfig, setGraphConfig] = useState(null);    // fetched config
  const [graphForm, setGraphForm] = useState({ tenant_id: '', client_id: '', client_secret: '', redirect_uri: '' });
  const [graphTesting, setGraphTesting] = useState(false);
  const [graphTestResult, setGraphTestResult] = useState(null);
  const [mailboxTesting, setMailboxTesting] = useState(false);
  const [mailboxTestResult, setMailboxTestResult] = useState(null);

  const openGraph = async (slug) => {
    setGraphProfile(slug);
    setGraphTestResult(null);
    setMailboxTestResult(null);
    setGraphForm({ tenant_id: '', client_id: '', client_secret: '', redirect_uri: '' });
    const r = await fetch(`${API}/api/profiles/${slug}/graph`, { credentials: 'include' });
    if (r.ok) {
      const d = await r.json();
      setGraphConfig(d);
      setGraphForm({
        tenant_id: d.tenant_id || '',
        client_id: d.client_id || '',
        client_secret: '',
        redirect_uri: d.redirect_uri || '',
      });
    }
  };

  const saveGraph = async () => {
    if (!graphProfile) return;
    const r = await fetch(`${API}/api/profiles/${graphProfile}/graph`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(graphForm),
    });
    if (r.ok) {
      const d = await fetch(`${API}/api/profiles/${graphProfile}/graph`, { credentials: 'include' }).then(r => r.json());
      setGraphConfig(d);
      setGraphForm(f => ({ ...f, client_secret: '' }));
    }
  };

  const testGraph = async () => {
    if (!graphProfile) return;
    setGraphTesting(true);
    setGraphTestResult(null);
    try {
      const r = await fetch(`${API}/api/profiles/${graphProfile}/graph/test`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      setGraphTestResult(d);
      const fresh = await fetch(`${API}/api/profiles/${graphProfile}/graph`, { credentials: 'include' }).then(r => r.json());
      setGraphConfig(fresh);
    } catch {
      setGraphTestResult({ connected: false, error: 'Verbindingsfout' });
    }
    setGraphTesting(false);
  };

  const testMailboxes = async () => {
    if (!graphProfile) return;
    setMailboxTesting(true);
    setMailboxTestResult(null);
    try {
      const r = await fetch(`${API}/api/profiles/${graphProfile}/graph/test-mailboxes`, {
        method: 'POST', credentials: 'include',
      });
      const d = await r.json();
      setMailboxTestResult(d);
    } catch {
      setMailboxTestResult({ summary: 'Verbindingsfout', mailboxes: [], all_ok: false });
    }
    setMailboxTesting(false);
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [pr, ur] = await Promise.all([
        fetch(`${API}/api/profiles/all`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/api/profiles/users`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      ]);
      if (pr) setProfiles(pr.profiles || []);
      if (ur) setUsers(ur.users || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const createProfile = async () => {
    setError('');
    const r = await fetch(`${API}/api/profiles`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createForm),
    });
    if (r.ok) {
      setShowCreate(false);
      setCreateForm({ slug: '', name: '', color: '#0F172A' });
      fetchAll();
      refetchProfileContext();  // sync sidebar profile-switcher
    } else {
      const d = await r.json().catch(() => ({}));
      setError(d.detail || 'Aanmaken mislukt');
    }
  };

  const deleteProfile = async (slug) => {
    if (!window.confirm(`Profiel '${slug}' verwijderen? Dit kan alleen als er geen data meer in zit.`)) return;
    const r = await fetch(`${API}/api/profiles/${slug}`, { method: 'DELETE', credentials: 'include' });
    if (r.ok) {
      fetchAll();
      refetchProfileContext();  // sync sidebar profile-switcher
    } else {
      const d = await r.json().catch(() => ({}));
      window.alert(d.detail || 'Verwijderen mislukt');
    }
  };

  const toggleAccess = async (user, slug) => {
    const has = (user.profile_access || []).includes(slug);
    const next = has
      ? (user.profile_access || []).filter(s => s !== slug)
      : [...(user.profile_access || []), slug];
    await fetch(`${API}/api/profiles/users/${user.user_id}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile_access: next }),
    });
    fetchAll();
  };

  const toggleSuper = async (user) => {
    await fetch(`${API}/api/profiles/users/${user.user_id}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_super_admin: !user.is_super_admin }),
    });
    fetchAll();
  };

  if (loading) return <div className="p-6 text-sm text-zinc-500">Laden...</div>;

  return (
    <div className="p-6 max-w-5xl" data-testid="profiles-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-zinc-900">Profielen</h1>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="new-profile-btn"
          className="flex items-center gap-2 px-3 py-2 text-xs font-medium bg-zinc-900 text-white hover:bg-black transition-colors"
        >
          <Plus size={12} weight="bold" /> Nieuw profiel
        </button>
      </div>
      <p className="text-sm text-zinc-500 mb-6">Beheer dedicated lead generators (PoolFIX, Evolution, ...) en welke gebruikers er toegang toe hebben.</p>

      {/* Profielen */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden mb-8">
        <table className="w-full text-sm" data-testid="profiles-table">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-4 py-3 text-left">Profiel</th>
              <th className="px-4 py-3 text-left w-32">Logo</th>
              <th className="px-4 py-3 text-right">Facilities</th>
              <th className="px-4 py-3 text-right">E-mails</th>
              <th className="px-4 py-3 text-right">Orders</th>
              <th className="px-4 py-3 text-right">Producten</th>
              <th className="px-4 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {profiles.map(p => (
              <tr key={p.slug} className="border-t border-zinc-100" data-testid={`profile-row-${p.slug}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                    <div>
                      <div className="font-semibold text-zinc-900">{p.name}</div>
                      <div className="text-[10px] text-zinc-400 font-mono">{p.slug}</div>
                    </div>
                    {p.is_default && <span className="ml-2 px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-zinc-100 text-zinc-600 rounded-sm">Standaard</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {p.logo_url ? (
                      <img
                        src={p.logo_url.startsWith('http') ? p.logo_url : `${API}${p.logo_url}`}
                        alt=""
                        className="h-7 max-w-[90px] object-contain border border-zinc-100 rounded-sm bg-white p-1"
                      />
                    ) : (
                      <span className="text-[10px] text-zinc-400 italic">Geen logo</span>
                    )}
                    <input
                      ref={el => (fileInputs.current[p.slug] = el)}
                      type="file"
                      accept=".png,.jpg,.jpeg,.svg,.webp"
                      className="hidden"
                      onChange={e => e.target.files[0] && uploadLogo(p.slug, e.target.files[0])}
                      data-testid={`logo-upload-input-${p.slug}`}
                    />
                    <button
                      onClick={() => fileInputs.current[p.slug]?.click()}
                      data-testid={`logo-upload-btn-${p.slug}`}
                      className="text-zinc-400 hover:text-zinc-700 transition-colors"
                      title="Upload logo"
                    >
                      <ImageIcon size={13} />
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-mono text-zinc-700">{p.stats?.facilities || 0}</td>
                <td className="px-4 py-3 text-right font-mono text-zinc-700">{p.stats?.emails || 0}</td>
                <td className="px-4 py-3 text-right font-mono text-zinc-700">{p.stats?.orders || 0}</td>
                <td className="px-4 py-3 text-right font-mono text-zinc-700">{p.stats?.products || 0}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => openGraph(p.slug)}
                    data-testid={`graph-config-${p.slug}`}
                    className="text-zinc-400 hover:text-blue-700 transition-colors mr-2"
                    title="MS Graph credentials"
                  >
                    <LinkIcon size={13} />
                  </button>
                  <button
                    onClick={() => {
                      setEditingProfile({
                        slug: p.slug,
                        name: p.name,
                        color: p.color,
                        tagline: p.tagline || '',
                        market_description: p.market_description || '',
                        product_context: p.product_context || '',
                        ai_instructions: p.ai_instructions || '',
                        sector_label: p.sector_label || '',
                      });
                      setAiTestResult(null);
                    }}
                    data-testid={`edit-profile-${p.slug}`}
                    className="text-zinc-400 hover:text-zinc-700 transition-colors mr-2"
                    title="Bewerken"
                  >
                    <Pencil size={13} />
                  </button>
                  {!p.is_default && (
                    <button
                      onClick={() => deleteProfile(p.slug)}
                      data-testid={`delete-profile-${p.slug}`}
                      className="text-zinc-400 hover:text-red-600 transition-colors"
                      title="Verwijder profiel"
                    >
                      <Trash size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Gebruikers + access matrix */}
      <h2 className="text-lg font-bold text-zinc-900 mb-2 flex items-center gap-2">
        <UsersIcon size={18} /> Gebruikerstoegang
      </h2>
      <p className="text-sm text-zinc-500 mb-3">Vink aan welke profielen elke gebruiker mag zien. Super-admins zien alles.</p>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-4 py-3 text-left">Gebruiker</th>
              <th className="px-4 py-3 text-center">Super-admin</th>
              {profiles.map(p => (
                <th key={p.slug} className="px-4 py-3 text-center">{p.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.user_id} className="border-t border-zinc-100" data-testid={`user-row-${u.user_id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-zinc-900">{u.name || u.email.split('@')[0]}</div>
                  <div className="text-[11px] text-zinc-500">{u.email}</div>
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => toggleSuper(u)}
                    data-testid={`super-${u.user_id}`}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm transition-colors ${
                      u.is_super_admin
                        ? 'bg-amber-100 text-amber-900 border border-amber-300'
                        : 'bg-zinc-50 text-zinc-400 border border-zinc-200 hover:border-zinc-400'
                    }`}
                  >
                    <ShieldStar size={11} weight={u.is_super_admin ? 'fill' : 'regular'} />
                    {u.is_super_admin ? 'Ja' : 'Nee'}
                  </button>
                </td>
                {profiles.map(p => {
                  const has = (u.profile_access || []).includes(p.slug) || u.is_super_admin;
                  return (
                    <td key={p.slug} className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={has}
                        disabled={u.is_super_admin}
                        onChange={() => toggleAccess(u, p.slug)}
                        data-testid={`access-${u.user_id}-${p.slug}`}
                        className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white border border-zinc-200 w-full max-w-md p-6 rounded-sm" onClick={e => e.stopPropagation()} data-testid="new-profile-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Nieuw profiel</h3>
              <button onClick={() => setShowCreate(false)} className="text-zinc-400 hover:text-zinc-700"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Slug (unieke identificatie)</label>
                <input
                  data-testid="new-profile-slug"
                  value={createForm.slug}
                  onChange={e => setCreateForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                  placeholder="evolution"
                  className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm font-mono focus:outline-none focus:border-zinc-900"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Naam</label>
                <input
                  data-testid="new-profile-name"
                  value={createForm.name}
                  onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Evolution Pools"
                  className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-900"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Kleur</label>
                <input
                  data-testid="new-profile-color"
                  type="color"
                  value={createForm.color}
                  onChange={e => setCreateForm(f => ({ ...f, color: e.target.value }))}
                  className="h-10 w-20 border border-zinc-200 rounded-sm cursor-pointer"
                />
              </div>
              {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">{error}</div>}
              <button
                onClick={createProfile}
                disabled={!createForm.slug || !createForm.name}
                data-testid="create-profile-submit"
                className="w-full px-3 py-2 text-sm font-semibold bg-zinc-900 text-white hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Aanmaken
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MS Graph modal */}
      {graphProfile && graphConfig && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setGraphProfile(null)}>
          <div className="bg-white border border-zinc-200 w-full max-w-lg p-6 rounded-sm max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="graph-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold">MS Graph credentials</h3>
                <p className="text-xs text-zinc-500">Profiel: {graphProfile}</p>
              </div>
              <button onClick={() => setGraphProfile(null)} className="text-zinc-400 hover:text-zinc-700"><X size={16} /></button>
            </div>

            {/* Status */}
            <div className={`flex items-center gap-2 px-3 py-2 mb-4 text-xs rounded-sm border ${
              graphConfig.status?.connected ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
              graphConfig.runtime_configured ? 'bg-amber-50 border-amber-200 text-amber-800' :
              'bg-zinc-50 border-zinc-200 text-zinc-600'
            }`}>
              {graphConfig.status?.connected
                ? <><CheckCircle size={14} weight="fill" /> Verbonden ({graphConfig.status.last_check_at?.slice(0,16)})</>
                : graphConfig.runtime_configured
                  ? <><WarningCircle size={14} weight="fill" /> {graphConfig.uses_env_fallback ? '.env fallback actief — niet getest' : 'Credentials ingesteld, niet getest'}</>
                  : <><WarningCircle size={14} /> Geen credentials</>}
            </div>
            {graphConfig.status?.error && (
              <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 px-3 py-2 mb-3 rounded-sm font-mono">{graphConfig.status.error}</div>
            )}

            {/* Setup wizard (collapsible) */}
            <GraphSetupWizard
              slug={graphProfile}
              form={graphForm}
              status={graphConfig.status}
              hasCredentials={graphConfig.has_credentials}
            />

            <div className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Tenant ID</label>
                <input
                  data-testid="graph-tenant-id"
                  value={graphForm.tenant_id}
                  onChange={e => setGraphForm(f => ({ ...f, tenant_id: e.target.value }))}
                  placeholder="bijv. 12345678-1234-..."
                  className="w-full px-3 py-2 text-sm font-mono border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-900"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Client ID</label>
                <input
                  data-testid="graph-client-id"
                  value={graphForm.client_id}
                  onChange={e => setGraphForm(f => ({ ...f, client_id: e.target.value }))}
                  placeholder="Application (client) ID"
                  className="w-full px-3 py-2 text-sm font-mono border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-900"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                  Client Secret {graphConfig.client_secret_set && <span className="ml-2 text-[9px] normal-case bg-zinc-100 px-1.5 py-0.5 rounded-sm tracking-normal">opgeslagen — laat leeg om te behouden</span>}
                </label>
                <input
                  data-testid="graph-client-secret"
                  type="password"
                  value={graphForm.client_secret}
                  onChange={e => setGraphForm(f => ({ ...f, client_secret: e.target.value }))}
                  placeholder={graphConfig.client_secret_set ? '••••••••' : 'Plak nieuw secret'}
                  className="w-full px-3 py-2 text-sm font-mono border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-900"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Redirect URI (optioneel)</label>
                <input
                  data-testid="graph-redirect-uri"
                  value={graphForm.redirect_uri}
                  onChange={e => setGraphForm(f => ({ ...f, redirect_uri: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-900"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={saveGraph}
                  data-testid="graph-save"
                  className="flex-1 px-3 py-2 text-sm font-semibold bg-zinc-900 text-white hover:bg-black transition-colors"
                >
                  Opslaan
                </button>
                <button
                  onClick={testGraph}
                  disabled={graphTesting || !graphConfig.has_credentials}
                  data-testid="graph-test"
                  className="flex-1 px-3 py-2 text-sm font-semibold border border-zinc-200 hover:border-zinc-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {graphTesting ? 'Testen...' : 'Verbinding testen'}
                </button>
              </div>

              {graphTestResult && (
                <div className={`text-xs px-3 py-2 rounded-sm ${
                  graphTestResult.connected ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  {graphTestResult.connected ? '✓ ' + graphTestResult.message : '✗ ' + (graphTestResult.error || 'Mislukt')}
                </div>
              )}

              {/* Per-mailbox toegangstest */}
              <div className="pt-3 border-t border-zinc-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500">Mailbox-toegang testen</span>
                  <button
                    onClick={testMailboxes}
                    disabled={mailboxTesting || !graphConfig.runtime_configured || !graphConfig.mailboxes?.length}
                    data-testid="mailbox-test-btn"
                    className="px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold border border-zinc-200 hover:border-zinc-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-sm"
                  >
                    {mailboxTesting ? 'Testen...' : 'Test alle mailboxen'}
                  </button>
                </div>
                <p className="text-[10px] text-zinc-500 mb-2">Probeert per mailbox de top-5 inbox items op te halen om te bevestigen dat de App-registratie ook écht toegang heeft.</p>

                {mailboxTestResult && (
                  <div className="space-y-1" data-testid="mailbox-test-results">
                    {mailboxTestResult.summary && !mailboxTestResult.mailboxes?.length && (
                      <div className="text-[11px] text-zinc-600 italic">{mailboxTestResult.summary}</div>
                    )}
                    {mailboxTestResult.mailboxes?.map((m, idx) => (
                      <div
                        key={m.mailbox}
                        data-testid={`mailbox-result-${idx}`}
                        className={`flex items-start gap-2 px-2 py-1.5 rounded-sm border text-[11px] ${
                          m.ok
                            ? 'bg-emerald-50 border-emerald-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <span className={`mt-0.5 inline-flex w-4 h-4 items-center justify-center rounded-full text-white font-bold text-[9px] ${m.ok ? 'bg-emerald-500' : 'bg-red-500'}`}>
                          {m.ok ? '✓' : '✗'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-medium truncate">{m.mailbox}</span>
                            {m.is_default && <span className="text-[8px] uppercase tracking-wider bg-zinc-100 text-zinc-600 px-1 py-0.5 rounded-sm">default</span>}
                          </div>
                          {m.ok ? (
                            <div className="text-[10px] text-emerald-700 mt-0.5">
                              {m.count} item{m.count !== 1 ? 's' : ''} gevonden{m.last_received && ` · laatste ${m.last_received}`}
                            </div>
                          ) : (
                            <div className="text-[10px] text-red-700 mt-0.5">
                              <code className="bg-white px-1 rounded">{m.error_code}</code>
                              {m.http_status && <span className="ml-1 text-zinc-500">HTTP {m.http_status}</span>}
                              {m.error_message && <div className="mt-0.5 break-words">{m.error_message}</div>}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {mailboxTestResult.total > 0 && (
                      <div className={`text-[10px] mt-1.5 ${mailboxTestResult.all_ok ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {mailboxTestResult.ok_count}/{mailboxTestResult.total} mailboxen toegankelijk
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Mailboxen onder dit profiel */}
              {graphConfig.mailboxes?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-zinc-100">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Gekoppelde mailboxen</div>
                  <div className="space-y-1">
                    {graphConfig.mailboxes.map(m => (
                      <div key={m.email} className="flex items-center gap-2 text-xs font-mono">
                        <span className={`w-1.5 h-1.5 rounded-full ${m.is_active ? 'bg-emerald-500' : 'bg-zinc-300'}`} />
                        <span>{m.email}</span>
                        {m.is_default && <span className="text-[9px] uppercase tracking-wider bg-zinc-100 px-1 py-0.5">default</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editingProfile && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditingProfile(null)}>
          <div className="bg-white border border-zinc-200 w-full max-w-3xl p-6 rounded-sm max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="edit-profile-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Profiel bewerken</h3>
              <button onClick={() => setEditingProfile(null)} className="text-zinc-400 hover:text-zinc-700"><X size={16} /></button>
            </div>

            {/* Basics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Naam</label>
                <input
                  data-testid="edit-profile-name"
                  value={editingProfile.name}
                  onChange={e => setEditingProfile(p => ({ ...p, name: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-900"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Hoofdkleur</label>
                <input
                  data-testid="edit-profile-color"
                  type="color"
                  value={editingProfile.color}
                  onChange={e => setEditingProfile(p => ({ ...p, color: e.target.value }))}
                  className="h-10 w-full border border-zinc-200 rounded-sm cursor-pointer"
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Tagline (optioneel)</label>
                <input
                  data-testid="edit-profile-tagline"
                  value={editingProfile.tagline || ''}
                  onChange={e => setEditingProfile(p => ({ ...p, tagline: e.target.value }))}
                  placeholder="Korte omschrijving"
                  className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-900"
                />
              </div>
            </div>

            {/* AI Kennis & Context */}
            <div
              className="border border-indigo-200 bg-indigo-50/30 rounded-sm p-4 mb-4"
              data-testid="ai-context-section"
            >
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-bold text-indigo-900">AI Kennis &amp; Context</h4>
                <span className="text-[10px] uppercase tracking-wider text-indigo-600">per profiel — strikt geïsoleerd</span>
              </div>
              <p className="text-xs text-indigo-700/80 mb-4">
                Deze velden bepalen wát de AI mag zeggen wanneer hij conceptantwoorden voor dit profiel schrijft.
                Andere profielen kunnen deze tekst nooit zien of gebruiken.
              </p>

              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-zinc-600 mb-1">Marktbeschrijving</label>
                  <textarea
                    data-testid="edit-profile-market"
                    rows={3}
                    value={editingProfile.market_description || ''}
                    onChange={e => setEditingProfile(p => ({ ...p, market_description: e.target.value }))}
                    placeholder="Wie zijn jullie doelgroep? Voorbeeld: 'Manegehouders, paardenfokkers en wedstrijdorganisaties in Nederland en België die hun stalbeheer willen digitaliseren.'"
                    className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-900 font-mono"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1">Optioneel — wordt als marktcontext meegegeven aan de AI als er geen specifieke product-kennis is ingevuld.</p>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-zinc-600 mb-1">Sector-label voor AVG-footer</label>
                  <input
                    data-testid="edit-profile-sector-label"
                    type="text"
                    value={editingProfile.sector_label || ''}
                    onChange={e => setEditingProfile(p => ({ ...p, sector_label: e.target.value }))}
                    placeholder={editingProfile.slug === 'poolfix' ? 'de zwembadsector' : `bv. 'de paardensportsector' of 'de markt waarin ${editingProfile.name || 'dit profiel'} actief is'`}
                    className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-900"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1">
                    Wordt gebruikt in de footer van uitgaande mails: <em>"U ontvangt deze email omdat uw organisatie actief is in {editingProfile.sector_label || (editingProfile.slug === 'poolfix' ? 'de zwembadsector' : `de markt waarin ${editingProfile.name || 'dit profiel'} actief is`)} (gerechtvaardigd belang, Art. 6.1.f AVG)."</em>
                  </p>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-zinc-600 mb-1">Product- / dienstkennis</label>
                  <textarea
                    data-testid="edit-profile-product-context"
                    rows={6}
                    value={editingProfile.product_context || ''}
                    onChange={e => setEditingProfile(p => ({ ...p, product_context: e.target.value }))}
                    placeholder="Beschrijf concreet welke producten / diensten dit profiel aanbiedt, prijzen, USP's, levertijden, kenmerken. Voorbeeld:&#10;HorseAppCompare biedt:&#10;- StableMate (€29/maand, ideaal voor manegehouders 5-30 paarden)&#10;- ProBreeder (€89/maand, voor fokkerijen met stamboomadministratie)&#10;Geen integraties met externe boekhouding — wel met KNHS-stamboeken."
                    className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-900 font-mono"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1">Belangrijkste veld — wordt letterlijk in de AI-prompt opgenomen bij elke offerte/vraag-antwoord.</p>
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-zinc-600 mb-1">AI-richtlijnen (do's &amp; don'ts)</label>
                  <textarea
                    data-testid="edit-profile-ai-instructions"
                    rows={5}
                    value={editingProfile.ai_instructions || ''}
                    onChange={e => setEditingProfile(p => ({ ...p, ai_instructions: e.target.value }))}
                    placeholder="Stijl, toon, taboes. Voorbeeld:&#10;- Schrijf altijd in je-vorm, nooit u-vorm&#10;- Vermeld nooit concrete kortingen — alleen 'maatwerk-prijs op aanvraag'&#10;- Bied altijd een gratis 14-daagse trial aan&#10;- Verwijs vragen over koppelingen met KNHS naar info@horseappcompare.com"
                    className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-900 font-mono"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1">Overrulet de standaard antwoordrichtlijnen. Laat leeg om de neutrale fallback te gebruiken.</p>
                </div>
              </div>

              {/* Live test */}
              <div className="mt-4 pt-4 border-t border-indigo-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold">Test concept genereren</span>
                  <button
                    onClick={runAiTest}
                    disabled={aiTesting}
                    data-testid="ai-context-test-btn"
                    className="px-3 py-1.5 text-[11px] font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors rounded-sm"
                  >
                    {aiTesting ? 'Bezig...' : 'Genereer voorbeeld-antwoord'}
                  </button>
                </div>
                <p className="text-[10px] text-zinc-600 mb-2">Test met onderstaande sample-mail of pas hem aan. De AI gebruikt de bovenstaande (nog niet opgeslagen) velden.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                  <input
                    data-testid="ai-context-sample-from"
                    value={aiSample.sample_from}
                    onChange={e => setAiSample(s => ({ ...s, sample_from: e.target.value }))}
                    placeholder="van@klant.nl"
                    className="px-2 py-1.5 text-xs border border-zinc-200 rounded-sm focus:outline-none focus:border-indigo-500 font-mono"
                  />
                  <input
                    data-testid="ai-context-sample-subject"
                    value={aiSample.sample_subject}
                    onChange={e => setAiSample(s => ({ ...s, sample_subject: e.target.value }))}
                    placeholder="Onderwerp"
                    className="md:col-span-2 px-2 py-1.5 text-xs border border-zinc-200 rounded-sm focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <textarea
                  data-testid="ai-context-sample-body"
                  rows={3}
                  value={aiSample.sample_body}
                  onChange={e => setAiSample(s => ({ ...s, sample_body: e.target.value }))}
                  placeholder="Sample-bericht van de klant..."
                  className="w-full px-2 py-1.5 text-xs border border-zinc-200 rounded-sm focus:outline-none focus:border-indigo-500"
                />

                {aiTestResult && (
                  <div className="mt-3" data-testid="ai-context-test-result">
                    {aiTestResult.ok ? (
                      <div className="bg-white border border-indigo-300 rounded-sm p-3 text-xs">
                        <div className="text-[10px] uppercase tracking-wider text-indigo-600 mb-1">AI-concept (preview)</div>
                        <div className="font-semibold text-zinc-900 mb-2">{aiTestResult.draft?.subject}</div>
                        <pre className="whitespace-pre-wrap text-zinc-700 font-sans">{aiTestResult.draft?.body_text}</pre>
                        <div className="text-[10px] text-zinc-500 mt-2">
                          Gebruikt: {aiTestResult.used?.market_description_chars} tekens markt · {aiTestResult.used?.product_context_chars} tekens product · {aiTestResult.used?.ai_instructions_chars} tekens richtlijnen
                        </div>
                      </div>
                    ) : (
                      <div className="bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2 rounded-sm">
                        ✗ {aiTestResult.error || 'Mislukt'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={saveEdit}
              data-testid="save-profile-edit"
              className="w-full px-3 py-2 text-sm font-semibold bg-zinc-900 text-white hover:bg-black transition-colors"
            >
              Opslaan
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
